import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { AdminHttpService } from './admin-http.service';

export interface IAdminPerson {
  org_person_id: number;
  person_id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  job_title: string | null;
  org_id: number;
  org_name: string;
  active: boolean;
}

export interface IAdminPeopleListResult {
  people: IAdminPerson[];
  total: number;
  page: number;
  per_page: number;
}

export interface IAdminPersonCreateInput {
  email_address: string;
  first_name: string;
  last_name: string;
  org_id: number;
  role_id: number;
  job_title?: string;
}

export interface IAdminPersonCreated {
  org_person_id: number;
  person_id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  job_title: string | null;
  org_id: number;
  org_name: string;
}

export interface IAdminRole {
  id: number;
  name: string | null;
}

export type AdminOllieState = 'none' | 'active' | 'expired';

export interface IAdminPersonDetail {
  org_person_id: number;
  person_id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  job_title: string | null;
  org_id: number;
  org_name: string;
  role_id: number;
  role_name: string;
  active: boolean;
  ollie_state: AdminOllieState;
  ollie_expiry_date: string | null;
  ollie_days_remaining: number | null;
}

export interface IAdminOllieGrantInput {
  org_person_id: number;
  expiry_date: string;
}

export interface IAdminOllieGrantResult {
  org_person_id: number;
  ollie_state: 'active';
  ollie_expiry_date: string;
  ollie_days_remaining: number;
}

export interface IAdminPersonActionResult {
  org_person_id: number;
  person_id: number;
  active: boolean;
}

export interface IAdminPersonUpdateInput {
  org_person_id: number;
  first_name: string;
  last_name: string;
  job_title: string | null;
  role_id: number;
}

export interface IAdminPersonAssessmentRow {
  org_person_assessment_id: number;
  assessment_name: string;
  iteration_name: string | null;
  status: 'Completed' | 'In progress' | 'Not started' | 'Expired';
  completed_at: string | null;
  iteration: number;
}

export interface IAdminPersonAssessmentsResult {
  items: IAdminPersonAssessmentRow[];
}

export interface IAdminMovePeopleInput {
  org_person_ids: number[];
  destination_org_id: number;
}

export interface IAdminMovePersonResult {
  org_person_id: number;
  first_name: string;
  last_name: string;
  outcome: 'moved' | 'skipped' | 'failed';
  reason: string | null;
}

export interface IAdminMovePeopleResult {
  moved_count: number;
  skipped_count: number;
  failed_count: number;
  results: IAdminMovePersonResult[];
}

@Injectable({
  providedIn: 'root',
})
export class AdminPeopleService {

  constructor(protected _http_service: AdminHttpService) {}

  createPerson(input: IAdminPersonCreateInput): Observable<IAdminPersonCreated> {
    return this._http_service
      .post<IAdminPersonCreated>('admin/people', input)
      .pipe(map((response) => response.data));
  }

  getRoles(): Observable<IAdminRole[]> {
    return this._http_service
      .get<IAdminRole[]>('admin/people/roles')
      .pipe(map((response) => response.data));
  }

  getPeople(
    page: number,
    perPage: number,
    status?: string,
    orgId?: number | null,
    search?: string,
    ollieState?: AdminOllieState | ''
  ): Observable<IAdminPeopleListResult> {
    let params = new HttpParams()
      .set('page', page)
      .set('per_page', perPage);
    if (status) {
      params = params.set('status', status);
    }
    if (orgId) {
      params = params.set('org_id', orgId);
    }
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params = params.set('search', trimmedSearch);
    }
    if (ollieState) {
      params = params.set('ollie_state', ollieState);
    }
    return this._http_service
      .get<IAdminPeopleListResult>('admin/people', params)
      .pipe(map((response) => response.data));
  }

  getPersonDetail(org_person_id: number): Observable<IAdminPersonDetail> {
    const params = new HttpParams().set('org_person_id', org_person_id);
    return this._http_service
      .get<IAdminPersonDetail>('admin/people/detail', params)
      .pipe(map((response) => response.data));
  }

  getPersonAssessments(org_person_id: number): Observable<IAdminPersonAssessmentsResult> {
    const params = new HttpParams().set('org_person_id', org_person_id);
    return this._http_service
      .get<IAdminPersonAssessmentsResult>('admin/people/assessments', params)
      .pipe(map((response) => response.data));
  }

  updatePerson(input: IAdminPersonUpdateInput): Observable<IAdminPersonDetail> {
    return this._http_service
      .put<IAdminPersonDetail>('admin/people', input)
      .pipe(map((response) => response.data));
  }

  deactivatePerson(org_person_id: number): Observable<IAdminPersonActionResult> {
    return this._http_service
      .post<IAdminPersonActionResult>('admin/people/deactivation', { org_person_id })
      .pipe(map((response) => response.data));
  }

  reactivatePerson(org_person_id: number): Observable<IAdminPersonActionResult> {
    return this._http_service
      .post<IAdminPersonActionResult>('admin/people/reactivation', { org_person_id })
      .pipe(map((response) => response.data));
  }

  movePeople(input: IAdminMovePeopleInput): Observable<IAdminMovePeopleResult> {
    return this._http_service
      .post<IAdminMovePeopleResult>('admin/people/move', input)
      .pipe(map((response) => response.data));
  }

  grantOllieAccess(input: IAdminOllieGrantInput): Observable<IAdminOllieGrantResult> {
    return this._http_service
      .post<IAdminOllieGrantResult>('admin/people/ollie-access', input)
      .pipe(map((response) => response.data));
  }

  // The 204 response carries no body, so HttpClient emits null rather than the
  // {data, code, message} envelope. Mapping `.data` off that would throw on the
  // success path, so this deliberately does not follow the deactivatePerson
  // shape above (B3).
  revokeOllieAccess(org_person_id: number): Observable<void> {
    const params = new HttpParams().set('org_person_id', org_person_id);
    return this._http_service
      .delete<void>('admin/people/ollie-access', params)
      .pipe(map(() => undefined));
  }
}
